/**
 * PersonaGroupDialogs — the five flows around Persona Groups: New group,
 * Create group from selection, Move (single + bulk), Add persona(s) to a
 * group, and Delete group (the centrepiece — two radio outcomes with
 * consequences quantified BEFORE the choice). Copy is reproduced verbatim
 * from the reviewed prototype (especially the delete dialog).
 *
 * Each dialog owns its own mutation (matching this file's existing
 * convention — see UpsertModuleModal/CreatePersonaModal in PersonasPage.tsx)
 * and receives already-fetched read data as props from the caller, which
 * already holds it via useQuery.
 */
import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "sonner";

import {
  ASSIGN_PERSONA_TO_GROUP_MUTATION,
  CREATE_PERSONA_GROUP_MUTATION,
  DELETE_PERSONA_GROUP_MUTATION,
  UPSERT_PERSONA_MODULE_MUTATION,
  type AcAdminPersona,
  type AcPersonaGroup,
  type AssignPersonaToGroupVars,
  type CreatePersonaGroupVars,
  type DeletePersonaGroupVars,
  type UpsertPersonaModuleVars,
} from "../graphql/adminOps";
import {
  computeCommonGrants,
  computeStripLiftedGrants,
  countSharedActions,
  formatNameList,
  summarizeDeleteGroupImpact,
  toCode,
  type UserCountState,
} from "../lib/personaGroupLogic";

const DIALOG_OVERLAY = "fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm";
const PANEL = "bg-card border border-border rounded-2xl shadow-xl w-full max-w-md mx-4 p-6";
const PANEL_LG = "bg-card border border-border rounded-2xl shadow-xl w-full max-w-xl mx-4 p-6 max-h-[90vh] overflow-y-auto";
const FIELD_LABEL = "block text-sm font-medium mb-1";
const TEXT_INPUT =
  "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring";
const BTN_GHOST =
  "px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_PRIMARY =
  "px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 hover:bg-primary/90 transition-colors";
const BTN_DANGER =
  "px-4 py-2 rounded-lg border border-destructive/40 text-destructive text-sm font-medium hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_DANGER_SOLID =
  "px-4 py-2 rounded-lg bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const CHOICE = "flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors";

// ---------------------------------------------------------------------------
// New group
// ---------------------------------------------------------------------------
export function NewGroupDialog({
  existingGroups,
  onClose,
  onCreated,
}: {
  existingGroups: { code: string; displayName: string }[];
  onClose: () => void;
  onCreated: (code: string) => void;
}) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);

  const [createGroup, { loading, error }] = useMutation<unknown, CreatePersonaGroupVars>(
    CREATE_PERSONA_GROUP_MUTATION,
    {
      onCompleted: () => {
        toast.success("Group created", { description: "Add permissions, then add personas." });
        onCreated(code);
        onClose();
      },
      onError: (e) => toast.error(`Couldn't create group: ${e.message}`),
    },
  );

  const trimmedName = name.trim();
  const derivedCode = codeEdited ? code : toCode(name);
  const clash = existingGroups.some(
    (g) => g.displayName.toLowerCase() === trimmedName.toLowerCase() || g.code === derivedCode,
  );
  const canSubmit = trimmedName.length > 0 && derivedCode.length > 0 && !clash && !loading;

  const submit = () => {
    if (!canSubmit) return;
    void createGroup({ variables: { code: derivedCode, displayName: trimmedName } });
  };

  return (
    <div className={DIALOG_OVERLAY} role="dialog" aria-modal="true" aria-label="New group">
      <div className={PANEL}>
        <h2 className="text-lg font-bold mb-1">New group</h2>
        <p className="text-sm text-muted-foreground mb-4">
          A group holds permissions that every persona in it receives. You can add personas after creating it.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col gap-4">
          <div>
            <label htmlFor="pg-name" className={FIELD_LABEL}>Name</label>
            <input
              id="pg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Care Team"
              autoComplete="off"
              className={TEXT_INPUT}
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="pg-code" className={FIELD_LABEL}>Code</label>
            <input
              id="pg-code"
              value={derivedCode}
              onChange={(e) => { setCodeEdited(true); setCode(e.target.value.toUpperCase()); }}
              placeholder="CARE_TEAM"
              autoComplete="off"
              className={TEXT_INPUT + " font-mono"}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Derived from the name. Used in APIs and audit logs; can&rsquo;t change later.
            </p>
          </div>
          {clash && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
              A group called {trimmedName || derivedCode} already exists.
            </p>
          )}
          {error && <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error.message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={BTN_GHOST}>Cancel</button>
            <button type="submit" disabled={!canSubmit} className={BTN_PRIMARY}>
              {loading ? "Creating…" : "Create group"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create group from selection
// ---------------------------------------------------------------------------
export function GroupFromSelectionDialog({
  selected,
  existingGroups,
  onClose,
  onCreated,
}: {
  selected: AcAdminPersona[];
  existingGroups: { code: string; displayName: string }[];
  onClose: () => void;
  onCreated: (code: string) => void;
}) {
  const [name, setName] = useState("");
  const [lift, setLift] = useState(true);

  const shared = useMemo(() => computeCommonGrants(selected), [selected]);
  const sharedModules = shared.length;
  const sharedActions = countSharedActions(shared);

  const [createGroup, { loading: creating, error: createError }] = useMutation<unknown, CreatePersonaGroupVars>(
    CREATE_PERSONA_GROUP_MUTATION,
  );
  const [assignToGroup] = useMutation<unknown, AssignPersonaToGroupVars>(ASSIGN_PERSONA_TO_GROUP_MUTATION);
  const [upsertPersonaModule] = useMutation<unknown, UpsertPersonaModuleVars>(UPSERT_PERSONA_MODULE_MUTATION);

  const trimmedName = name.trim();
  const code = toCode(trimmedName);
  const clash = existingGroups.some((g) => g.displayName.toLowerCase() === trimmedName.toLowerCase());
  const canSubmit = trimmedName.length > 0 && !clash && !creating;

  const submit = async () => {
    if (!canSubmit) return;
    const willLift = lift && sharedModules > 0;
    try {
      await createGroup({
        variables: {
          code,
          displayName: trimmedName,
          modules: willLift ? shared.map((s) => ({ moduleId: s.moduleId, sortOrder: 0, actions: s.actions })) : undefined,
        },
      });
      await Promise.all(selected.map((p) => assignToGroup({ variables: { personaCode: p.code, groupCode: code } })));

      // Only AFTER the group + memberships are confirmed do we strip the
      // lifted grants from each persona's own rows — this is what makes
      // "removing them from each persona" actually true (the group now
      // covers them, so a later edit to the group reaches every member
      // instead of stopping at whoever still holds a duplicate own copy).
      // Promise.allSettled, never Promise.all: a partial failure here must
      // leave a DUPLICATE grant, not a missing one — too much access is
      // recoverable, a lockout mid-shift is not.
      let stripFailures = 0;
      if (willLift) {
        const stripResults = await Promise.allSettled(
          selected.flatMap((p) =>
            computeStripLiftedGrants(p.code, p.modules, shared).map((vars) => upsertPersonaModule({ variables: vars })),
          ),
        );
        stripFailures = stripResults.filter((r) => r.status === "rejected").length;
      }

      toast.success(`Group created from ${selected.length} personas`, {
        description: willLift
          ? `${sharedModules} shared module${sharedModules === 1 ? "" : "s"} lifted into the group. Effective permissions unchanged.`
          : "The group starts empty; the personas kept their own permissions.",
      });
      if (stripFailures > 0) {
        toast.warning("Some personas still hold a duplicate copy of the lifted grants", {
          description: "Nothing was lost — those personas just have an extra own copy on top of the group's. Open them and clean it up when convenient.",
        });
      }
      onCreated(code);
      onClose();
    } catch (e) {
      toast.error(`Couldn't create group: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className={DIALOG_OVERLAY} role="dialog" aria-modal="true" aria-label="Create group from selection">
      <div className={PANEL_LG}>
        <h2 className="text-lg font-bold">Create group from {selected.length} personas</h2>
        <p className="text-sm text-muted-foreground mb-4">{selected.map((p) => p.displayName).join(", ")}</p>
        <div className="flex flex-col gap-4">
          <div>
            <label htmlFor="pgs-name" className={FIELD_LABEL}>Group name</label>
            <input
              id="pgs-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Clinical Review"
              autoComplete="off"
              className={TEXT_INPUT}
              autoFocus
            />
            {clash && (
              <p className="text-sm text-destructive mt-1">A group called {trimmedName} already exists.</p>
            )}
          </div>

          {sharedModules === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
              These personas share no permissions, so there is nothing to lift. The group will start empty and
              they&rsquo;ll keep everything they have.
            </div>
          ) : (
            <label className="flex gap-3 p-3 rounded-xl border border-primary/30 bg-primary/5 text-sm cursor-pointer">
              <input type="checkbox" checked={lift} onChange={(e) => setLift(e.target.checked)} className="mt-0.5 accent-primary shrink-0" />
              <span>
                <b className="font-semibold">
                  Lift the {sharedModules} shared module{sharedModules === 1 ? "" : "s"}
                  {sharedActions > 0 ? ` and ${sharedActions} shared action grant${sharedActions === 1 ? "" : "s"}` : ""} into
                  the group
                </b>
                , removing them from each persona. Because a group&rsquo;s grants are always received, nothing changes
                about what these personas can do.
                <span className="flex flex-wrap gap-1.5 mt-2">
                  {shared.map((s) => (
                    <span
                      key={s.moduleId}
                      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold bg-primary/10 text-primary border border-primary/45"
                    >
                      {s.moduleId}
                      {s.actions.length > 0 ? ` · ${s.actions.join(", ")}` : ""}
                    </span>
                  ))}
                </span>
              </span>
            </label>
          )}

          {createError && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{createError.message}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={BTN_GHOST}>Cancel</button>
            <button type="button" onClick={() => void submit()} disabled={!canSubmit} className={BTN_PRIMARY}>
              {creating ? "Creating…" : "Create group"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Move to group / Ungrouped (single + bulk)
// ---------------------------------------------------------------------------
export function MoveDialog({
  personas,
  groups,
  onClose,
  onMoved,
}: {
  personas: AcAdminPersona[];
  groups: AcPersonaGroup[];
  onClose: () => void;
  onMoved: (groupCode: string | null) => void;
}) {
  const one = personas.length === 1 ? personas[0] : null;
  const current = one ? one.groupCode : undefined;
  const [target, setTarget] = useState<string | null>(current !== undefined ? current : null);

  const [assign, { loading }] = useMutation<unknown, AssignPersonaToGroupVars>(ASSIGN_PERSONA_TO_GROUP_MUTATION, {
    onError: (e) => toast.error(`Couldn't move: ${e.message}`),
  });

  const submit = async () => {
    try {
      await Promise.all(personas.map((p) => assign({ variables: { personaCode: p.code, groupCode: target } })));
      toast.success(personas.length === 1 ? "Persona moved" : `${personas.length} personas moved`, {
        description: target
          ? `Now inheriting from ${groups.find((g) => g.code === target)?.displayName ?? target}.`
          : "Inherited permissions were copied across; nothing was lost.",
      });
      onMoved(target);
      onClose();
    } catch {
      // toast already shown via onError
    }
  };

  return (
    <div className={DIALOG_OVERLAY} role="dialog" aria-modal="true" aria-label="Move personas">
      <div className={PANEL}>
        <h2 className="text-lg font-bold">{one ? `Move ${one.displayName}` : `Move ${personas.length} personas`}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Moving into a group adds the group&rsquo;s permissions on top of what the persona already has. Nothing is
          ever removed by a move.
        </p>
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <label key={g.code} className={CHOICE + (target === g.code ? " border-primary/60 bg-primary/5" : " border-border hover:bg-accent")}>
              <input type="radio" name="mv" checked={target === g.code} onChange={() => setTarget(g.code)} className="mt-1 accent-primary shrink-0" />
              <span>
                <span className="block text-sm font-semibold">{g.displayName}</span>
                <span className="block text-xs text-muted-foreground">
                  Grants {g.modules.length} modules to {g.personaCodes.length} personas
                  {current === g.code ? " · current group" : ""}
                </span>
              </span>
            </label>
          ))}
          <label className={CHOICE + (target === null ? " border-primary/60 bg-primary/5" : " border-border hover:bg-accent")}>
            <input type="radio" name="mv" checked={target === null} onChange={() => setTarget(null)} className="mt-1 accent-primary shrink-0" />
            <span>
              <span className="block text-sm font-semibold">Ungrouped</span>
              <span className="block text-xs text-muted-foreground">
                Manages its own permissions. Anything currently inherited will be copied onto it first, so nothing is
                lost.
              </span>
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className={BTN_GHOST}>Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={loading} className={BTN_PRIMARY}>
            {loading ? "Moving…" : "Move"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add persona(s) to a group
// ---------------------------------------------------------------------------
export function AddMemberDialog({
  group,
  candidates,
  onClose,
  onAdded,
}: {
  group: AcPersonaGroup;
  candidates: AcAdminPersona[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [assign, { loading }] = useMutation<unknown, AssignPersonaToGroupVars>(ASSIGN_PERSONA_TO_GROUP_MUTATION, {
    onError: (e) => toast.error(`Couldn't add: ${e.message}`),
  });

  if (candidates.length === 0) {
    return (
      <div className={DIALOG_OVERLAY} role="dialog" aria-modal="true" aria-label="No ungrouped personas">
        <div className={PANEL}>
          <h2 className="text-lg font-bold mb-1">No ungrouped personas</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Every persona already belongs to a group. Move one out of its current group first — a persona can only be
            in one group.
          </p>
          <div className="flex justify-end">
            <button type="button" onClick={onClose} className={BTN_PRIMARY}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const toggle = (code: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const submit = async () => {
    const chosen = [...picked];
    if (chosen.length === 0) return;
    try {
      await Promise.all(chosen.map((code) => assign({ variables: { personaCode: code, groupCode: group.code } })));
      toast.success(`${chosen.length} persona${chosen.length === 1 ? "" : "s"} added`, {
        description: `Now inheriting ${group.modules.length} modules from ${group.displayName}.`,
      });
      onAdded();
      onClose();
    } catch {
      // toast already shown via onError
    }
  };

  return (
    <div className={DIALOG_OVERLAY} role="dialog" aria-modal="true" aria-label={`Add personas to ${group.displayName}`}>
      <div className={PANEL_LG}>
        <h2 className="text-lg font-bold">Add personas to {group.displayName}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          They keep everything they have and gain the group&rsquo;s {group.modules.length} modules on top.
        </p>
        <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
          {candidates.map((p) => (
            <label
              key={p.code}
              className={CHOICE + (picked.has(p.code) ? " border-primary/60 bg-primary/5" : " border-border hover:bg-accent")}
            >
              <input type="checkbox" checked={picked.has(p.code)} onChange={() => toggle(p.code)} className="mt-1 accent-primary shrink-0" />
              <span>
                <span className="block text-sm font-semibold">
                  {p.displayName} <span className="font-mono font-normal opacity-60">{p.code}</span>
                </span>
                <span className="block text-xs text-muted-foreground">
                  {p.modules.length} own modules · will gain {group.modules.length} inherited modules
                </span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onClose} className={BTN_GHOST}>Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={picked.size === 0 || loading} className={BTN_PRIMARY}>
            {loading ? "Adding…" : picked.size ? `Add ${picked.size} persona${picked.size === 1 ? "" : "s"}` : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DELETE GROUP — the centrepiece.
// ---------------------------------------------------------------------------
export function DeleteGroupDialog({
  group,
  members,
  onClose,
  onDeleted,
}: {
  group: AcPersonaGroup;
  members: { code: string; displayName: string; userCount: UserCountState }[];
  onClose: () => void;
  onDeleted: (outcome: "UNGROUP" | "DELETE_PERSONAS") => void;
}) {
  const [mode, setMode] = useState<"UNGROUP" | "DELETE_PERSONAS">("UNGROUP");
  const [confirmText, setConfirmText] = useState("");

  const impact = useMemo(() => summarizeDeleteGroupImpact(group, members), [group, members]);
  const listNames = formatNameList(members.map((m) => m.displayName));
  // The destructive option must never be chosen against a number we haven't
  // actually measured — "0 users lose access" while deleting personas real
  // people hold is exactly the dangerous case a confident-but-wrong count
  // would produce. Disabled (not defaulted to 0) until every member's user
  // count has resolved.
  const userCountUnknown = impact.userCount === null;

  const [deleteGroup, { loading }] = useMutation<unknown, DeletePersonaGroupVars>(DELETE_PERSONA_GROUP_MUTATION, {
    onError: (e) => toast.error(`Couldn't delete group: ${e.message}`),
  });

  const hard = mode === "DELETE_PERSONAS";
  const confirmMatches = confirmText.trim() === group.displayName;
  const canSubmit = !loading && (!hard || (confirmMatches && !userCountUnknown));

  const submit = async () => {
    if (!canSubmit) return;
    try {
      await deleteGroup({ variables: { groupCode: group.code, onMembers: mode } });
      if (hard) {
        toast.error(`Deleted "${group.displayName}" and ${members.length} personas`, {
          description: `${impact.userCount} users lost access. Not reversible.`,
        });
      } else {
        toast.success(`Deleted "${group.displayName}", kept ${members.length} personas`, {
          description: userCountUnknown
            ? "Its grants were copied onto each one — effective permissions unchanged."
            : `Its grants were copied onto each one — effective permissions unchanged for ${impact.userCount} users.`,
        });
      }
      onDeleted(mode);
      onClose();
    } catch {
      // toast already shown via onError
    }
  };

  return (
    <div className={DIALOG_OVERLAY} role="dialog" aria-modal="true" aria-label={`Delete ${group.displayName}`}>
      <div className={PANEL_LG}>
        <h2 className="text-lg font-bold">Delete &ldquo;{group.displayName}&rdquo;?</h2>
        <p className="text-sm text-muted-foreground mb-4">
          This group grants <b className="font-semibold text-foreground">{impact.moduleCount}</b> modules
          {impact.actionGrantCount > 0 && (
            <> and <b className="font-semibold text-foreground">{impact.actionGrantCount}</b> action grants</>
          )}{" "}
          to <b className="font-semibold text-foreground">{impact.memberCount}</b> persona{impact.memberCount === 1 ? "" : "s"}
          {userCountUnknown ? (
            <>, held by an unknown number of users (still loading — hold on before deleting).</>
          ) : (
            <>
              , held by <b className="font-semibold text-foreground">{impact.userCount}</b> user{impact.userCount === 1 ? "" : "s"}.
            </>
          )}
        </p>

        {members.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground mb-4">
            No personas belong to this group, so deleting it affects nobody.
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-4">
            <label
              className={CHOICE + (mode === "UNGROUP" ? " border-primary/60 bg-primary/5" : " border-border hover:bg-accent")}
            >
              <input type="radio" name="del" checked={mode === "UNGROUP"} onChange={() => setMode("UNGROUP")} className="mt-1 accent-primary shrink-0" />
              <span>
                <span className="block text-sm font-semibold">Keep the personas</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {group.displayName}&rsquo;s {impact.moduleCount} module grants are{" "}
                  <b className="font-semibold text-foreground">copied onto each persona</b>, so nothing changes
                  {userCountUnknown ? (
                    <> for the users who hold them.</>
                  ) : (
                    <>
                      {" "}
                      for the <b className="tabular-nums">{impact.userCount}</b> users who hold them.
                    </>
                  )}{" "}
                  The personas become independent.
                </span>
              </span>
            </label>

            <label
              className={
                "flex flex-col gap-2 p-3 rounded-xl border transition-colors " +
                (userCountUnknown
                  ? "border-border opacity-60 cursor-not-allowed"
                  : hard
                    ? "border-destructive/55 bg-destructive/[0.07] cursor-pointer"
                    : "border-border hover:bg-accent cursor-pointer")
              }
            >
              <span className="flex gap-3">
                <input
                  type="radio"
                  name="del"
                  checked={hard}
                  disabled={userCountUnknown}
                  onChange={() => setMode("DELETE_PERSONAS")}
                  className="mt-1 accent-destructive shrink-0"
                />
                <span>
                  <span className={"block text-sm font-semibold " + (hard ? "text-destructive" : "")}>
                    Delete the personas too
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {userCountUnknown ? (
                      <>User counts are still loading — wait before choosing this option so the impact above is accurate.</>
                    ) : (
                      <>
                        {listNames} {members.length === 1 ? "is" : "are"} deleted with the group.{" "}
                        <b className="tabular-nums">{impact.userCount}</b> user{impact.userCount === 1 ? "" : "s"} lose
                        access to everything {members.length === 1 ? "it grants" : "those personas grant"}. This cannot
                        be undone.
                      </>
                    )}
                  </span>
                </span>
              </span>
              {hard && (
                <span className="pl-7 flex flex-col gap-1">
                  <label htmlFor="del-confirm" className="text-xs font-semibold">
                    Type <span className="font-mono">{group.displayName}</span> to confirm
                  </label>
                  <input
                    id="del-confirm"
                    autoComplete="off"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className={TEXT_INPUT}
                    autoFocus
                  />
                </span>
              )}
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={BTN_GHOST}>Cancel</button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className={hard ? BTN_DANGER_SOLID : BTN_DANGER}
          >
            {loading
              ? "Deleting…"
              : hard
                ? `Delete group and ${members.length} persona${members.length === 1 ? "" : "s"}`
                : "Delete group, keep personas"}
          </button>
        </div>
      </div>
    </div>
  );
}

